import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { CartHeaderBlockConfig } from '@front-platform/content-builder';
import { cartHeaderBlockResolveConfig } from '@front-platform/content-builder';
import { map, take } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { Component, OnInit, input } from '@angular/core';

import { ISimpleItem } from '@core/models/simple-item.model';
import { ShopStoreRefiner } from '@common/refiners/shop.refiner';
import { DistributionModeValues } from '@config/values/order.values';
import { DistributionModeKeys, MarketplaceKeys } from '@config/keys/shop.keys';

import { CartStoreSelector } from '@common/selectors/cart.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';

import { CartParametersService } from '@core/services/cart-parameters/cart-parameters.service';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
export interface CartHeaderBlockRuntimeConfig {}

export type CartHeaderBlockConfigInput = Partial<
  Omit<CartHeaderBlockConfig, keyof CartHeaderBlockRuntimeConfig> & CartHeaderBlockRuntimeConfig
>;
export type CartHeaderBlockResolvedConfig = Omit<
  CartHeaderBlockConfig,
  keyof CartHeaderBlockRuntimeConfig
> &
  Partial<CartHeaderBlockRuntimeConfig>;
export interface CartHeaderBlockContract extends ConfigComponentContract<
  CartHeaderBlockConfigInput,
  CartHeaderBlockResolvedConfig
> {}

@Component({
  selector: 'app-cart-header-block',
  templateUrl: './cart-header-block.component.html',
  standalone: false,
})
export class CartHeaderBlockComponent implements OnInit, CartHeaderBlockContract {
  readonly config = input<CartHeaderBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    CartHeaderBlockConfig,
    CartHeaderBlockRuntimeConfig
  >(cartHeaderBlockResolveConfig, () => ({ config: this.config() }));
  get textColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor;
  }
  get backgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor;
  }
  get labelColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['label_color'] as unknown as IContentBuilderFieldColor;
  }
  get iconColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['icon_color'] as unknown as IContentBuilderFieldColor;
  }
  get iconBackgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['icon_background_color'] as unknown as IContentBuilderFieldColor;
  }

  parameters!: ISimpleItem[];

  hideCartParameters$: Observable<boolean> = combineLatest([
    this.shopRefiner.selectActiveShopEnabledDistributionMode,
    this.marketplaceSelector.selectMarketplace$,
  ]).pipe(
    map(([shopDistributionModes, marketplace]) => {
      const isMutiShop = marketplace[MarketplaceKeys.CartScope] === 'MARKETPLACE';

      const distributionModes = isMutiShop
        ? marketplace[MarketplaceKeys.DistributionModes]?.filter(
            mode => mode?.[DistributionModeKeys.Enabled],
          )
        : shopDistributionModes;

      return (
        distributionModes?.length === 1 &&
        distributionModes[0][DistributionModeKeys.Type] === DistributionModeValues.EatIn
      );
    }),
  );

  constructor(
    private shopRefiner: ShopStoreRefiner,
    private cartSelector: CartStoreSelector,
    private marketplaceSelector: MarketplaceStoreSelector,
    private readonly cartParametersService: CartParametersService,
    private readonly navigationHistoryService: NavigationHistoryService,
  ) {}

  ngOnInit(): void {
    this.cartSelector.selectActive$
      .pipe(
        take(1),
        map(cart => {
          if (cart) {
            this.parameters = this.cartParametersService.retrieveCartParametersValue(cart);
          }
        }),
      )
      .subscribe();
  }

  back(): void {
    this.navigationHistoryService.goBack();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(CartHeaderBlockComponent, { 'keyAliases': {} });
